export class EmptyOptionsError extends Error {
  constructor(message = "Nothing to pick from — add some options first.") {
    super(message);
  }
}

export function randomFloat(): number {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return a[0] / 2 ** 32;
}

export function randomInt(min: number, max: number): number {
  const lo = Math.ceil(Math.min(min, max));
  const hi = Math.floor(Math.max(min, max));
  return lo + Math.floor(randomFloat() * (hi - lo + 1));
}

export function weightedPick<T>(items: T[], weightOf: (t: T) => number = () => 1): T {
  if (!items.length) throw new EmptyOptionsError();
  const weights = items.map((i) => Math.max(0, weightOf(i)));
  const total = weights.reduce((a, b) => a + b, 0);
  if (total <= 0) return items[randomInt(0, items.length - 1)];
  let r = randomFloat() * total;
  for (let i = 0; i < items.length; i++) {
    r -= weights[i];
    if (r < 0) return items[i];
  }
  return items[items.length - 1];
}

export function pickExcluding<T>(items: T[], exclude?: T, weightOf?: (t: T) => number): T {
  const pool = exclude !== undefined && items.length > 1 ? items.filter((i) => i !== exclude) : items;
  return weightedPick(pool, weightOf);
}

export function binaryPick<A, B>(a: A, b: B): A | B {
  return randomFloat() < 0.5 ? a : b;
}

export function numericPick(min: number, max: number): number {
  return randomInt(min, max);
}

export function shuffle<T>(items: T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomInt(0, i);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function sampleN<T>(items: T[], n: number): T[] {
  return shuffle(items).slice(0, n);
}

export function unique(items: string[]): string[] {
  const seen = new Set<string>();
  return items.filter((i) => {
    const k = i.trim().toLowerCase();
    if (!k || seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
